import { useState, useCallback } from 'react';

export const useBiblicalMetrics = () => {
  const [metrics, setMetrics] = useState({
    currentlyExploring: '',
    topicsDiscovered: new Set(),
    explorationChains: new Map(),
    deepestDive: { topic: '', depth: 0, path: [] }
  });

  // Build exploration path for a given node
  const buildPath = useCallback((nodeId, chains) => {
    const path = [];
    let currentId = nodeId;

    while (currentId && chains.has(currentId)) {
      const node = chains.get(currentId);
      path.unshift(node.topic);
      currentId = node.parentId;
    }

    return path;
  }, []);

  // Calculate depth of exploration chain
  const calculateDepth = useCallback((nodeId, chains) => {
    let depth = 1;
    let currentId = nodeId;

    while (chains.has(currentId) && chains.get(currentId).parentId) {
      depth++;
      currentId = chains.get(currentId).parentId;
    }

    return depth;
  }, []);

  // Update metrics when a new node is added (simplified version)
  const updateMetrics = useCallback((nodeData, parentNodeId = null) => {
    const topic = nodeData.currentlyExploring;
    if (!topic || typeof topic !== 'string' || topic.trim() === '') {
      return;
    }

    const cleanTopic = topic.trim();

    setMetrics(prev => {
      // Add to topics discovered
      const newTopics = new Set([...prev.topicsDiscovered, cleanTopic]);

      // Update exploration chains
      const newChains = new Map(prev.explorationChains);

      // Calculate depth for this node
      let depth = 1;
      if (parentNodeId && newChains.has(parentNodeId)) {
        depth = newChains.get(parentNodeId).depth + 1;
      }

      // Store chain info for this node
      newChains.set(nodeData.nodeId, {
        topic: cleanTopic,
        depth,
        parentId: parentNodeId,
        timestamp: Date.now()
      });

      // Update deepest dive if this chain is longer
      let newDeepestDive = prev.deepestDive;
      if (depth > prev.deepestDive.depth) {
        const path = buildPath(nodeData.nodeId, newChains);
        newDeepestDive = {
          topic: cleanTopic,
          depth,
          path
        };
      }

      return {
        currentlyExploring: cleanTopic,
        topicsDiscovered: newTopics,
        explorationChains: newChains,
        deepestDive: newDeepestDive
      };
    });
  }, [buildPath]);

  return {
    metrics,
    updateMetrics
  };
};

export default useBiblicalMetrics;